import { PageHeader } from "@/components/data-display/page-header";
import { StatCard } from "@/components/data-display/stat-card";
import { KVList } from "@/components/data-display/kv-list";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { IdentifierValue } from "@/components/ui/identifier-value";
import { NotAvailableState, LoadingState } from "@/components/ui/system-state";
import { CanarySourceReview } from "@/features/provider-migration/canary-source-review";
import { ProviderMigrationNav } from "@/features/provider-migration/journey-nav";
import { CATEGORY_LABEL, snapshotFreshness, UnavailableWithRetry } from "@/features/provider-migration/shared";
import { useProviderMigrationSummary } from "@/hooks/use-provider-migration";

/**
 * Provider migration, step 3: import review (page spec provider-migration-import-review).
 * Reviews what a read-only import would cover, from the real sealed snapshot. Destination mapping
 * to Mesh wallets has no backend in this environment, so that block is an honest not-available
 * state: nothing is classified, mapped or approved here.
 *
 * "Canary source review" scopes the same sealed read to one source vault. It is inspection only:
 * the choice is local, it records no plan or approval, and canary execution stays unavailable.
 */

export default function ProviderMigrationImportReviewPage() {
  const s = useProviderMigrationSummary();
  const categories = [s.vaults.data, s.wallets.data, s.addresses.data];
  // Keep the reported summary field distinct from the strict per-category source review below.
  const freshness = snapshotFreshness(s.freshUntil);
  // A category that did not come back at all is not a complete read and is never labelled as one.
  const allCategoriesRead = categories.every((category) => category != null);

  return (
    <div>
      <PageHeader
        title="Import review"
        subtitle="What a read-only import would cover, from the latest sealed provider snapshot."
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={s.refresh}>
              Refresh
            </Button>
          </>
        }
      />
      <ProviderMigrationNav active="import-review" />
      <Badge variant="neutral">Read-only, no funds moved</Badge>

      {s.loading && <LoadingState rows={4} />}
      {!s.loading && s.error && (
        <UnavailableWithRetry title="Import review is not available" error={s.error} onRetry={s.refresh} />
      )}

      {!s.loading && !s.error && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatCard value={s.vaults.data ? String(s.vaults.data.items.length) : "Not reported"} label={CATEGORY_LABEL.VAULT_ACCOUNTS} />
            <StatCard value={s.wallets.data ? String(s.wallets.data.items.length) : "Not reported"} label={CATEGORY_LABEL.ASSET_WALLETS} />
            <StatCard value={s.addresses.data ? String(s.addresses.data.items.length) : "Not reported"} label={CATEGORY_LABEL.VAULT_ADDRESSES} />
          </div>

          <section aria-labelledby="readiness-heading" className="max-w-2xl">
            <h2 id="readiness-heading" className="mb-2 text-base font-bold text-ink">Snapshot readiness</h2>
            <p className="mb-2 text-xs text-muted">
              Freshness describes the summary timestamp at the last read. Source review below checks each
              inventory category and its expiry.
            </p>
            <KVList
              rows={[
                { k: "Snapshot", v: s.snapshotId ? <IdentifierValue value={s.snapshotId} /> : "None sealed yet" },
                {
                  k: "Freshness",
                  v: (
                    <Badge variant={freshness === "fresh" ? "success" : freshness === "stale" ? "warning" : "neutral"}>
                      {freshness === "fresh" ? "Fresh" : freshness === "stale" ? "Stale" : "Unknown"}
                    </Badge>
                  ),
                },
                {
                  k: "Inventory completeness",
                  // Only a read that returned all three categories can be called complete or partial.
                  v: !allCategoriesRead ? (
                    <Badge variant="neutral">Not reported</Badge>
                  ) : s.truncated ? (
                    <Badge variant="warning">Partial (read cap reached)</Badge>
                  ) : (
                    <Badge variant="success">Complete as read</Badge>
                  ),
                },
                {
                  k: "Total items discovered",
                  v: allCategoriesRead ? String(s.total) : "Not reported",
                },
              ]}
            />
          </section>

          <CanarySourceReview
            vaults={s.vaults.data}
            assetWallets={s.wallets.data}
            vaultAddresses={s.addresses.data}
            onRefresh={s.refresh}
          />

          <section aria-labelledby="mapping-heading">
            <h2 id="mapping-heading" className="mb-2 text-base font-bold text-ink">Destination mapping</h2>
            <NotAvailableState
              title="Destination mapping is not available in this environment yet"
              description="Mapping source wallets to Mesh wallets, and classifying each item as supported or needing review, has no backend here. Nothing on this page is mapped, approved or scheduled."
            />
          </section>
        </div>
      )}
    </div>
  );
}
